import { useState, useEffect } from 'react';

export interface Recommendation {
  type: string;
  summary: string;
  recommendation: string[];
  followup_questions_to_ask_customer: string[];
  next_best_actions_by_agent: string[];
  tools_used: string[];
  suggested_message?: string;
  timestamp?: string;
  showLoadingBar?: boolean;
  isAdhoc?: boolean;
}

export const useRecommendations = (initialSessionId?: string) => {
  const [wsContactId, setWsContactId] = useState('');
  const [wsStatus, setWsStatus] = useState<'disconnected' | 'connecting' | 'connected' | 'error'>('disconnected');
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [isTypingRecommendation, setIsTypingRecommendation] = useState(false);
  const [recommendationMode, setRecommendationMode] = useState<'live' | 'adhoc'>('adhoc');
  const [isRequestingAdhoc, setIsRequestingAdhoc] = useState(false);
  const [requestAdhocRecommendationFn, setRequestAdhocRecommendationFn] = useState<(() => void) | null>(null);

  // Auto-initialize WebSocket if sessionId is available
  useEffect(() => {
    if (initialSessionId) {
      setWsContactId(initialSessionId);
    }
  }, [initialSessionId]);

  const handleRecommendation = (recommendation: Recommendation) => {
    if (recommendation.type === 'no_useful_info') return;
    setRecommendations(prev => [recommendation, ...prev]);
  };

  const handleModeChange = (mode: 'live' | 'adhoc') => {
    setRecommendationMode(mode);
  };

  const handleModeChangeComplete = () => {
    console.log(`Recommendation mode changed to: ${recommendationMode}`);
  };

  const handleRequestAdhocRecommendation = () => {
    if (wsStatus === 'connected' && requestAdhocRecommendationFn) {
      setIsRequestingAdhoc(true);
      requestAdhocRecommendationFn();
      
      setTimeout(() => {
        setIsRequestingAdhoc(false);
      }, 5000);
    }
  };

  const handleRequestAdhocRecommendationReady = (requestFn: () => void) => {
    setRequestAdhocRecommendationFn(() => requestFn);
  };

  const initializeWebSocket = (contactId: string) => {
    setWsContactId(contactId);
  };

  return {
    // State
    wsContactId,
    wsStatus,
    recommendations,
    isTypingRecommendation,
    recommendationMode,
    isRequestingAdhoc,
    
    // Handlers
    handleRecommendation,
    handleModeChange,
    handleModeChangeComplete,
    handleRequestAdhocRecommendation,
    handleRequestAdhocRecommendationReady,
    initializeWebSocket,
    
    // Setters for WebSocket Manager
    setWsStatus: setWsStatus,
    setIsTypingRecommendation: setIsTypingRecommendation,
  };
};
